import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import type {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';

/** Top level of the media menu. Every picker splits into these two before offering a source. */
export enum MediaSourceGroup {
  IMAGE = 'image',
  VIDEO = 'video',
}

/** Second level: where the media comes from. */
export enum MediaSourceKind {
  UPLOADS = 'uploads',
  STOCK = 'stock',
  AI = 'ai',
}

/**
 * What picking a leaf should DO. The menu shape is identical across surfaces but the mutation is
 * not — appending a background to the timeline sequence, setting the page background, and swapping
 * the selected media are three different writes, so intent travels with every request.
 */
export enum MediaSourceIntent {
  APPEND_BACKGROUND = 'append-background',
  SET_BACKGROUND = 'set-background',
  REPLACE_BACKGROUND = 'replace-background',
  REPLACE_ITEM = 'replace-item',
}

/** One selectable option: a group crossed with a source. Six exist in a full menu. */
export interface MediaSourceLeaf {
  group: MediaSourceGroup;
  kind: MediaSourceKind;
}

export type ReplaceableMediaItem = ImageItem | ImageSlideItem | VideoItem | VideoSlideItem;

/**
 * A resolved pick, ready for the dispatcher. Item replacement is the only intent that needs a
 * target and a write-back callback, so it carries them and the background intents cannot.
 */
export type MediaSourceRequest =
  | {
      intent: MediaSourceIntent.APPEND_BACKGROUND | MediaSourceIntent.SET_BACKGROUND | MediaSourceIntent.REPLACE_BACKGROUND;
      leaf: MediaSourceLeaf;
    }
  | {
      intent: MediaSourceIntent.REPLACE_ITEM;
      leaf: MediaSourceLeaf;
      item: ReplaceableMediaItem;
      onReplace: (itemData: ItemData) => void;
    };

/** A leaf as the renderers need it: display data plus the click that dispatches it. */
export interface MediaSourceOption {
  leaf: MediaSourceLeaf;
  id: string;
  text: string;
  icon: string;
  /** Sub-text for the roomier flat rows; the compact flyout rows ignore it. */
  subText: string;
  isAi: boolean;
  onSelect: () => void;
}

/** How a group row is drawn. Each surface keeps whatever row style its other options already use. */
export enum MediaSourceRowStyle {
  /** Icon + label + caret. Matches the right panel and timeline popups. */
  COMPACT = 'compact',
  /** Thumbnail tile + label + sub-text. Matches the add-item bar's roomier rows. */
  DETAILED = 'detailed',
}

/** One `Image ▸` / `Video ▸` branch. */
export interface MediaSourceGroupNode {
  group: MediaSourceGroup;
  id: string;
  text: string;
  icon: string;
  /** Only drawn by the DETAILED row style. */
  subText: string;
  options: MediaSourceOption[];
}

export interface BuildMediaSourceGroupsOptions {
  /** Defaults to both groups. Narrow to IMAGE for sticker replace, which has no video counterpart. */
  groups?: MediaSourceGroup[];
  /** Defaults to all three kinds. Drop AI where the surface or the user is not eligible. */
  kinds?: MediaSourceKind[];
  /** Sticker replace relabels the AI image leaf to "AI Stickers" and swaps its icon. */
  useStickerLabelForAiImage?: boolean;
  onSelect: (leaf: MediaSourceLeaf) => void;
}

export const MEDIA_SOURCE_FLYOUT_HOVER_DELAY_MS = 300;

/** The design-system class that tints AI affordances. */
export const AI_CONTENT_CLASS = 'content-ai';

export interface MediaSourceMenuProps {
  groups: MediaSourceGroupNode[];
  /**
   * Which side the group flyouts open on. Defaults to opening leftward, which suits the right-hand
   * editing panel; surfaces anchored on the left of the screen pass RIGHT_START.
   */
  flyoutAlignment?: ALIGNMENT_TYPE;
  /** Defaults to COMPACT. */
  rowStyle?: MediaSourceRowStyle;
  /**
   * Draws a pointer on the flyout aimed back at its row, so it reads as belonging to Image or Video
   * rather than to the menu as a whole. Off by default; the add-item bar's popups all use one.
   */
  showFlyoutArrow?: boolean;
}
